import fileTypeIcon from '@assets/images/fileTypeIcon.svg'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { z } from 'zod'
import { ButtonBrandLegacy } from '@/components/ui/button-brand-legacy'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { DataTable } from '@/components/ui/data-table'
import { formatDate } from '@/dates'
import { useAuthStore } from '@/features/auth/store/store'
import { CsvUploadDialog } from '@/features/luxe-submissions/components/csv-upload-dialog'
import { EmptyStateListings } from '@/features/luxe-submissions/components/empty-state-listings'
import { ReviewerNameLabel } from '@/features/luxe-submissions/components/reviewer-name-label'
import { useGetCsvTemplate } from '@/features/luxe-submissions/queries/use-get-csv-template'
import { csvUploadQueryOptions } from '@/features/luxe-submissions/queries/use-get-csv-upload'
import { useGetCsvUploads } from '@/features/luxe-submissions/queries/use-get-csv-uploads'
import type { CsvUpload } from '@/features/luxe-submissions/types'
import {
	csvUploadStatusToBadgeStyle,
	csvUploadStatusToLabelTKey
} from '@/features/luxe-submissions/utils'
import { useParsedSearchParams } from '@/hooks/use-parsed-search-params'
import { useRowsPerPage } from '@/hooks/use-rows-per-page'
import { useTranslation } from '@/translations/i18n'

const CsvUploadsSearchParamsSchema = z.object({
	sortBy: z.string().optional(),
	sortOrder: z.enum(['asc', 'desc']).optional(),
	page: z.string().optional(),
	perPage: z.string().optional()
})

function LuxeSubmissionsCsvUploads() {
	const { t } = useTranslation()
	const user = useAuthStore((state) => state.user)
	const { data: template, isLoading: isTemplateLoading } = useGetCsvTemplate()

	const { searchParams, setSearchParams } = useParsedSearchParams(
		CsvUploadsSearchParamsSchema
	)
	const currentPage = searchParams.page ? Number(searchParams.page) : 1

	const { rowsPerPage, setRowsPerPage, totalRows, reportTotal } =
		useRowsPerPage({
			searchParam: searchParams.perPage,
			setSearchParam: (newPerPage) =>
				setSearchParams({ perPage: newPerPage, page: undefined })
		})

	const { data, isLoading } = useGetCsvUploads({
		page: currentPage,
		perPage: rowsPerPage
	})

	const queryClient = useQueryClient()
	const pollingUploadIds = useMemo(
		() =>
			(data?.data ?? [])
				.filter((u) => u.status === 'processing')
				.map((u) => u.id),
		[data?.data]
	)
	// polling uploads that are still processing
	useQueries({
		queries: pollingUploadIds.map((id) =>
			csvUploadQueryOptions(id, queryClient)
		)
	})

	const lastPage = data?.meta.lastPage
	reportTotal(data?.meta.total)

	const setPage = (page: number) => {
		setSearchParams({ page: page === 1 ? undefined : String(page) })
	}

	const columns = useMemo(
		() => [
			{
				id: 'file' as const,
				header: t('luxe_submissions:csv_uploads_column_file'),
				cell: ({ dataItem }: { dataItem: CsvUpload }) => (
					<div className='flex items-center gap-3'>
						<img
							alt={dataItem.fileName}
							height={40}
							src={fileTypeIcon}
							width={40}
						/>
						<div className='flex min-w-0 flex-col text-sm'>
							<span className='truncate font-medium text-neutral-900'>
								{dataItem.fileName}
							</span>
							<span className='text-neutral-600'>
								{formatDate(
									dataItem.uploadedAt ?? '',
									'MMM d, yyyy · h:mm a'
								)}
							</span>
						</div>
					</div>
				),
				size: 320
			},
			{
				id: 'uploadedBy' as const,
				header: t('luxe_submissions:csv_uploads_column_uploaded_by'),
				cell: ({ dataItem }: { dataItem: CsvUpload }) => {
					const isMe =
						String(user?.id) === String(dataItem.uploadedByUserId)

					return (
						<ReviewerNameLabel
							email={t(
								'luxe_submissions:csv_uploads_user_fallback',
								{
									id: dataItem.uploadedByUserId
								}
							)}
							firstName={
								dataItem.uploadedByUserName?.split(' ')[0] ?? ''
							}
							isMe={isMe}
							lastName={
								dataItem.uploadedByUserName?.split(' ')[1] ?? ''
							}
						/>
					)
				},
				size: 240
			},
			{
				id: 'rowsOk' as const,
				header: t('luxe_submissions:csv_uploads_column_rows_ok'),
				cell: ({ dataItem }: { dataItem: CsvUpload }) => {
					const ok = Math.max(
						dataItem.processedRows - dataItem.failedRows,
						0
					)
					return (
						<span
							className={`font-medium text-sm ${ok > 0 ? 'text-success-600' : 'text-neutral-900'}`}
						>
							{ok.toLocaleString()}
						</span>
					)
				},
				size: 140
			},
			{
				id: 'rowsWithErrors' as const,
				header: t(
					'luxe_submissions:csv_uploads_column_rows_with_errors'
				),
				cell: ({ dataItem }: { dataItem: CsvUpload }) => (
					<span
						className={`font-medium text-sm ${dataItem.failedRows > 0 ? 'text-danger-600' : 'text-neutral-900'}`}
					>
						{dataItem.failedRows.toLocaleString()}
					</span>
				),
				size: 180
			},
			{
				id: 'status' as const,
				header: t('luxe_submissions:csv_uploads_column_status'),
				cell: ({ dataItem }: { dataItem: CsvUpload }) => (
					<span
						className={`inline-flex rounded-full border px-2 py-0.5 font-medium text-xs ${csvUploadStatusToBadgeStyle[dataItem.status]}`}
					>
						{t(csvUploadStatusToLabelTKey[dataItem.status])}
					</span>
				),
				size: 140
			}
		],
		[t, user]
	)

	return (
		<div className='flex flex-1 flex-col overflow-hidden px-10 py-6'>
			<DataTable
				columns={columns}
				data={data?.data ?? []}
				emptyComponent={
					<EmptyStateListings
						action={
							<div className='flex items-center gap-3'>
								<ButtonLegacy
									asChild
									disabled={isTemplateLoading || !template}
									variant='secondary'
								>
									<a
										download={template?.fileName ?? ''}
										href={template?.url ?? ''}
									>
										{t(
											'luxe_submissions:csv_uploads_download_template'
										)}
									</a>
								</ButtonLegacy>

								<CsvUploadDialog
									trigger={
										<ButtonBrandLegacy
											size='md'
											type='button'
										>
											{t(
												'luxe_submissions:csv_uploads_upload_first_file'
											)}
										</ButtonBrandLegacy>
									}
								/>
							</div>
						}
						className='justify-baseline py-12'
						description={t(
							'luxe_submissions:csv_uploads_empty_description'
						)}
						title={t('luxe_submissions:csv_uploads_empty_title')}
					/>
				}
				isLoading={isLoading}
				lastPage={lastPage}
				page={currentPage}
				rowsPerPage={rowsPerPage}
				setPage={setPage}
				setRowsPerPage={setRowsPerPage}
				totalRows={totalRows}
			/>
		</div>
	)
}

export { LuxeSubmissionsCsvUploads }
